import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useUserRole } from '../../hooks/useUserRole';
import clsx from 'clsx';
import { useInboxStore, statusPastBijFilter } from '../../stores/inbox-store';
import { resolveAssigneeName, assigneeInitials } from './AssigneePicker';
import { SnoozeMenu, formatSnoozeUntil } from './SnoozeMenu';
import { ShortcutHelp } from './ShortcutHelp';
import { OutboundComposerModal } from '../outbound/OutboundComposerModal';
import { sorteerInboxWerkvoorraad } from '../../lib/inbox/werkstap';
import { classicAfhandelen } from '../../lib/inbox/classic-safety';

/**
 * Linker kolom van split-screen inbox: filters + tabs + threadlijst.
 *
 * Geconsumeerd door <InboxPage> in batch D4.
 * In tussentijd standalone bouw-baar — tot D4 is bestaande InboxPage onveranderd.
 *
 * Gedrag:
 * - Klik op thread = setActiveThread(id) (in-page state-swap, geen route-nav).
 *   Caller kan onSelectThread overriden voor backwards-compat met route-nav.
 * - Geactiveerde thread (activeThread.id === conv.id) krijgt highlight.
 * - Open e-mails tonen de volgende werkstap; overige concepten een ✨ indicator.
 * - Keyboard: ↑↓/j/k = navigate, Enter = select, E = afhandelen, I = in-behandeling.
 * - Missed Calls tab vervangt oude banner.
 */

const BRAND_TABS = [
  { label: 'Alle', value: 'all' },
  { label: 'Kleda', value: 'KLEDA' },
  { label: 'Cinco', value: 'CINCO' },
  { label: 'SHS', value: 'SCHOONHUISSHOP' },
  { label: 'SofaSaver', value: 'SOFASAVER' },
];

// Banen (migratie 235). Medewerkers zien alleen Klant (de tabs blijven dan weg);
// admins zien de rest. Dit is de demo, niet het product: de handlers per baan
// draaien zonder dit scherm. Persoonlijke mailboxen (w.blok@) komen hier niet
// meer in: die worden in Gmail behandeld (plan 2026-10-31), de tab "Mijn mail"
// is 02-11-2026 weggehaald.
const BAAN_TABS: Array<{ label: string; value: string }> = [
  { label: 'Klant', value: 'klant' },
  { label: 'Systeem', value: 'systeem' },
  { label: 'Leverancier', value: 'leverancier' },
  { label: 'Nieuwsbrief', value: 'nieuwsbrief' },
  { label: 'Intern', value: 'intern' },
  { label: 'Onbekend', value: 'onbekend' },
  { label: 'Alles', value: 'all' },
];

// Telefoon is uit de inbox — gemist/voicemail leven als opvolgtaken in
// /telefoon > Opvolgen. De inbox is puur mail + WhatsApp.
const CHANNEL_TABS: Array<{ label: string; value: 'all' | 'email' | 'whatsapp' | 'phone' }> = [
  { label: 'Alle', value: 'all' },
  { label: 'Email', value: 'email' },
  { label: 'WhatsApp', value: 'whatsapp' },
];

const STATUS_TABS = [
  { label: 'Alle', value: 'all' },
  { label: 'Open', value: 'open' },
  { label: 'Mijn', value: 'mijn' },
  { label: 'In behandeling', value: 'in_behandeling' },
  { label: 'Later', value: 'later' },
  { label: 'Beantwoord', value: 'beantwoord' },
  { label: 'Afgehandeld', value: 'afgehandeld' },
  { label: 'Bounces', value: 'bounce' },
];

const BRAND_COLORS: Record<string, string> = {
  KLEDA: 'bg-emerald-100 text-emerald-700',
  CINCO: 'bg-blue-100 text-blue-700',
  SCHOONHUISSHOP: 'bg-teal-100 text-teal-700',
  SOFASAVER: 'bg-orange-100 text-orange-700',
};

const STATUS_COLORS: Record<string, string> = {
  nieuw: 'bg-blue-500',
  in_behandeling: 'bg-amber-500',
  beantwoord: 'bg-emerald-500',
  afgehandeld: 'bg-slate-300',
  bounce: 'bg-red-400',
};

function formatTime(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  if (days === 1) return 'gisteren';
  if (days < 7) return date.toLocaleDateString('nl-NL', { weekday: 'short' });
  return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

function ConversationSkeleton() {
  return (
    <div className="px-6 py-4 flex items-start gap-3 animate-pulse">
      <div className="pt-2 w-2 flex-shrink-0">
        <div className="w-2 h-2 rounded-full bg-slate-200" />
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center gap-2">
          <div className="h-4 bg-slate-200 rounded w-32" />
          <div className="h-4 bg-slate-100 rounded w-12" />
        </div>
        <div className="h-3.5 bg-slate-200 rounded w-3/4" />
        <div className="h-3 bg-slate-100 rounded w-1/2" />
      </div>
      <div className="flex-shrink-0">
        <div className="h-3 bg-slate-100 rounded w-10" />
      </div>
    </div>
  );
}

/**
 * V3: werkstand per gesprek in gewone woorden, en de werkvoorraad daarop
 * gesorteerd (beslissing nodig → klaar om te versturen → geen concept → rest).
 * Gebruikt de werkstap die de server al berekent; geen extra request.
 */
// Werkstanden in gewone woorden (Willem 14-09 en 17-09):
//   Beslissing nodig  = er staat een kaart open (Ja/Nee) of het gesprek is urgent;
//                       sinds 17-09 weer zichtbaar (verborgen las als "niks aan de hand")
//   Klaar             = concept staat klaar, alleen lezen en versturen
//   Handeling open    = er is een concept, maar met een handeling die geen verb dekt
//                       (het signaal voor een ontbrekend verb); was tot 17-09 "Geen concept"
//   Geen concept      = de lezer heeft (nog) niets of de uitvoering faalde; zelf schrijven of Opnieuw
//   Systeemmail       = no-reply/autoreply zonder klant, de lezer slaat bewust over; lezen en sluiten
//   Sluiten?          = lijkt bulk/automatisch verkeer; controleren en sluiten zonder antwoord
// Alles wat de klant bij ons weg heeft gelegd: Wacht op klant.
const V3_WERKSTAND: Partial<Record<string, { label: string; prio: number; klas: string }>> = {
  wacht_op_akkoord: { label: 'Beslissing nodig', prio: 0, klas: 'bg-indigo-50 text-indigo-700' },
  nu: { label: 'Beslissing nodig', prio: 0, klas: 'bg-indigo-50 text-indigo-700' },
  antwoord_beoordelen: { label: 'Klaar', prio: 1, klas: 'bg-emerald-50 text-emerald-700' },
  handeling_ontbreekt: { label: 'Handeling open', prio: 2, klas: 'bg-amber-50 text-amber-700' },
  concept_ontbreekt: { label: 'Geen concept', prio: 3, klas: 'bg-slate-100 text-slate-600' },
  mislukt: { label: 'Geen concept', prio: 3, klas: 'bg-slate-100 text-slate-600' },
  systeemmail: { label: 'Systeemmail', prio: 4, klas: 'bg-slate-100 text-slate-500' },
  sluiten_beoordelen: { label: 'Sluiten?', prio: 4, klas: 'bg-slate-100 text-slate-500' },
};
function v3Werkstand(conv: { mail_werkstap?: { code?: string } | null; status: string; prebaked_draft?: boolean | null }) {
  if (conv.status === 'beantwoord') return { label: 'Wacht op klant', prio: 5, klas: 'bg-slate-100 text-slate-500' };
  const code = conv.mail_werkstap?.code;
  if (code && V3_WERKSTAND[code]) return V3_WERKSTAND[code]!;
  if (code === 'geen_antwoord_nodig') return null;
  if (conv.prebaked_draft) return V3_WERKSTAND.antwoord_beoordelen!;
  return null;
}

export interface ThreadListProps {
  variant: 'classic' | 'v2' | 'v3';
  /**
   * Override voor thread-selectie. Default: store.setActiveThread (in-page).
   * Backwards-compat: pass `(id) => navigate('/inbox/' + id)` voor route-nav.
   */
  onSelectThread?: (id: string) => void;
}

/**
 * Snippet uit de sync bevat HTML-entiteiten en het begin van het geciteerde
 * bericht ("Op 14 sep 2026 ... heeft Kleda geschreven"). In een Gmail-rij
 * telt alleen wat de klant zelf schreef.
 */
export function schoneSnippet(raw: string | null | undefined): string {
  if (!raw) return '';
  let t = raw.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&');
  t = t.split(/\s(?=(?:Op|On)\s+(?:\w+,?\s+)?\d{1,2}(?:\s+\w+\.?\s+|\/\d{2}\/)\d{4}|Verzonden (?:vanuit|vanaf|met)|Verstuurd vanaf|Sent from|Van:\s|From:\s|-{2,}\s*(?:Oorspronkelijk|Original))/i)[0] ?? t;
  return t.replace(/\s+/g, ' ').trim();
}

export function ThreadList({ onSelectThread, variant }: ThreadListProps) {
  const isV2 = variant === 'v2';
  const isV3 = variant === 'v3';
  const [classicError, setClassicError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [showShortcutHelp, setShowShortcutHelp] = useState(false);
  const [showNewEmail, setShowNewEmail] = useState(false);
  const {
    conversations,
    isLoading,
    isLoadingMore,
    brandFilter,
    statusFilter,
    channelFilter,
    baanFilter,
    baanTellers,
    searchQuery,
    total,
    nieuwCount,
    inBehandelingCount,
    bounceCount,
    mijnCount,
    laterCount,
    teamMembers,
    hasMore,
    isSyncStale,
    threadsError,
    activeThread,
    setBrandFilter,
    setStatusFilter,
    setChannelFilter,
    setBaanFilter,
    setBaanTabsZichtbaar,
    setSearchQuery,
    setActiveThread,
    updateStatus,
    loadMore,
    fetchTeam,
    selectNextThread,
    selectPrevThread,
  } = useInboxStore();
  const activeIdVoorToetsen = activeThread?.conversation.id ?? null;
  const { role } = useUserRole();
  const toonBaanTabs = role === 'admin';
  useEffect(() => { setBaanTabsZichtbaar(toonBaanTabs); }, [toonBaanTabs, setBaanTabsZichtbaar]);
  const [searchInput, setSearchInput] = useState('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isWerkvoorraad = ['open', 'nieuw', 'mijn', 'in_behandeling'].includes(statusFilter);
  const getoondeConversations = useMemo(
    () => isV2 && isWerkvoorraad ? sorteerInboxWerkvoorraad(conversations)
      // V3: nieuwste bovenaan, net als de andere tabs (Willem 17-09). De
      // werkstand blijft als badge zichtbaar maar sorteert niet meer: de
      // prio-groepen maakten de lijst onleesbaar (13:56, 13:36, 13:10, 13:46, …).
      : isV3 && isWerkvoorraad
        ? [...conversations].sort((a, b) => (Date.parse(b.last_message_at || '') || 0) - (Date.parse(a.last_message_at || '') || 0))
        : conversations,
    [conversations, isWerkvoorraad, isV2, isV3],
  );
  const nuCount = getoondeConversations.filter((conversation) => conversation.inbox_meta?.urgentie.nu).length;

  const handleSelect = useCallback((id: string) => {
    if (onSelectThread) onSelectThread(id);
    else void setActiveThread(id);
  }, [onSelectThread, setActiveThread]);

  useEffect(() => {
    setDismissedIds(new Set());
  }, [statusFilter, brandFilter, channelFilter, baanFilter]);

  // Teamleden laden zodat eigenaar-chips meteen initialen/namen tonen
  useEffect(() => {
    void fetchTeam();
  }, [fetchTeam]);

  const getStatusCount = (tabValue: string): number | null => {
    if (tabValue === 'all') return total;
    if (tabValue === 'open') return nieuwCount + inBehandelingCount;
    if (tabValue === 'nieuw') return nieuwCount;
    if (tabValue === 'mijn') return mijnCount;
    if (tabValue === 'in_behandeling') return inBehandelingCount;
    if (tabValue === 'later') return laterCount;
    if (tabValue === 'bounce') return bounceCount;
    return null;
  };

  const handleDismiss = useCallback((convId: string, newStatus: string) => {
    if (!isV2 && newStatus === 'afgehandeld') {
      const conv = conversations.find(c => c.id === convId);
      if (!conv) return;
      setClassicError(null);
      void classicAfhandelen(conv)
        .then(() => useInboxStore.getState().fetchConversations())
        .catch(error => setClassicError(error instanceof Error ? error.message : 'Afhandelen mislukt.'));
      return;
    }
    const willLeaveList = !statusPastBijFilter(statusFilter, newStatus);
    if (willLeaveList) {
      setDismissedIds(prev => new Set(prev).add(convId));
      setTimeout(() => {
        updateStatus(convId, newStatus);
        setDismissedIds(prev => {
          const next = new Set(prev);
          next.delete(convId);
          return next;
        });
        setSelectedIndex(prev => Math.min(prev, getoondeConversations.length - 2));
      }, 200);
    } else {
      updateStatus(convId, newStatus);
    }
  }, [updateStatus, getoondeConversations.length, statusFilter, isV2, conversations]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return;

    // V3 (Gmail-indeling): staat een gesprek open, dan is de lijst verborgen.
    // j/k bladeren dan door de gesprekken; e/i/Enter zijn van ThreadDetail.
    if (isV3 && activeIdVoorToetsen) {
      if (e.key === 'ArrowDown' || e.key === 'j') { e.preventDefault(); void selectNextThread(); }
      else if (e.key === 'ArrowUp' || e.key === 'k') { e.preventDefault(); void selectPrevThread(); }
      else if (e.key === '?') { e.preventDefault(); setShowShortcutHelp(true); }
      return;
    }

    if (e.key === 'ArrowDown' || e.key === 'j') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, getoondeConversations.length - 1));
    } else if (e.key === 'ArrowUp' || e.key === 'k') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && selectedIndex >= 0 && selectedIndex < getoondeConversations.length) {
      e.preventDefault();
      const conv = getoondeConversations[selectedIndex];
      if (conv) handleSelect(conv.id);
    } else if (e.key === 'e' && !e.metaKey && !e.ctrlKey && !e.altKey) {
      if (selectedIndex >= 0 && selectedIndex < getoondeConversations.length) {
        const conv = getoondeConversations[selectedIndex];
        if (conv && conv.status !== 'afgehandeld' && conv.status !== 'bounce' && (!isV2 || !conv.inbox_meta?.blokkeertAfhandelen)) {
          e.preventDefault();
          handleDismiss(conv.id, 'afgehandeld');
        }
      }
    } else if (e.key === 'i' && !e.metaKey && !e.ctrlKey && !e.altKey) {
      if (selectedIndex >= 0 && selectedIndex < getoondeConversations.length) {
        const conv = getoondeConversations[selectedIndex];
        if (conv && conv.status !== 'afgehandeld' && conv.status !== 'bounce') {
          e.preventDefault();
          handleDismiss(conv.id, 'in_behandeling');
        }
      }
    } else if (e.key === '?') {
      e.preventDefault();
      setShowShortcutHelp(true);
    }
  }, [getoondeConversations, selectedIndex, handleSelect, handleDismiss, isV3, activeIdVoorToetsen, selectNextThread, selectPrevThread]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    setSelectedIndex(-1);
    setSearchInput('');
  }, [brandFilter, statusFilter, channelFilter, baanFilter]);

  useEffect(() => {
    if (selectedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-conv-item]');
      items[selectedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  // Infinite scroll: sentinel onder de lijst triggert loadMore zodra hij in
  // beeld komt. De "Meer laden"-knop blijft staan als fallback.
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) void loadMore();
      },
      { root: listRef.current, rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, loadMore]);

  const activeId = activeThread?.conversation.id ?? null;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className={clsx('px-6 border-b border-slate-200 bg-white', isV3 ? 'py-3' : 'py-4')}>
        <div className={clsx('flex items-center justify-between', isV3 ? 'mb-2' : 'mb-4')}>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-slate-900">Inbox</h1>
            {/* Vrije outbound-mail — ook naar iemand zonder klantrecord; het
                verstuurde bericht wordt een nieuw gesprek in deze lijst. */}
            <button
              onClick={() => setShowNewEmail(true)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-slate-500 border border-slate-200 hover:text-indigo-600 hover:border-indigo-300 transition-colors"
              title="Nieuwe e-mail sturen (hoeft geen bestaande klant te zijn)"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Nieuwe e-mail
            </button>
          </div>
          <div className="flex items-center gap-3">
            {isSyncStale && (
              <div className="flex items-center gap-1.5 text-amber-600 text-sm">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span>Sync gestopt</span>
              </div>
            )}
            <span className="text-sm text-slate-500">{total} gesprekken</span>
            {isLoading && (
              <div className="w-3 h-3 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            )}
          </div>
        </div>

        {/* Brand filter */}
        <div className={clsx('flex gap-1 flex-wrap', isV3 ? 'inline-flex mr-6 mb-2 align-middle' : 'mb-3')}>
          {BRAND_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setBrandFilter(tab.value)}
              className={clsx(
                'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                brandFilter === tab.value ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Baan-tabs (alleen admin) */}
        {toonBaanTabs && (
          <div className={clsx('flex gap-1 flex-wrap items-center', isV3 ? 'inline-flex mr-6 mb-2 align-middle' : 'mb-3')}>
            {BAAN_TABS.map((tab) => {
              const teller = baanTellers && tab.value !== 'all' ? baanTellers[tab.value] : undefined;
              return (
                <button
                  key={tab.value}
                  onClick={() => setBaanFilter(tab.value)}
                  className={clsx(
                    'px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5',
                    baanFilter === tab.value ? 'bg-amber-50 text-amber-800' : 'text-slate-500 hover:bg-slate-50'
                  )}
                  title={tab.value === 'all' ? 'Alle banen' : `Baan ${tab.label.toLowerCase()}`}
                >
                  {tab.label}
                  {typeof teller === 'number' && teller > 0 && (
                    <span className="text-[11px] px-1.5 rounded-full bg-white/70 text-slate-600">{teller}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Channel filter + Missed Calls tab */}
        <div className={clsx('flex gap-1 flex-wrap items-center', isV3 ? 'inline-flex mb-2 align-middle' : 'mb-3')}>
          {CHANNEL_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setChannelFilter(tab.value)}
              className={clsx(
                'px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5',
                channelFilter === tab.value ? 'bg-emerald-50 text-emerald-700' : 'text-slate-500 hover:bg-slate-50'
              )}
            >
              {tab.value === 'whatsapp' && (
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              )}
              {tab.value === 'email' && (
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
              )}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Status filter */}
        {isV3 && <div aria-hidden />}
        <div className={clsx('flex gap-1 flex-wrap', isV3 && 'inline-flex align-middle')}>
          {STATUS_TABS.map((tab) => {
            const count = getStatusCount(tab.value);
            return (
              <button
                key={tab.value}
                onClick={() => setStatusFilter(tab.value)}
                className={clsx(
                  'px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5',
                  statusFilter === tab.value ? 'bg-indigo-50 text-indigo-700' : 'text-slate-500 hover:bg-slate-50'
                )}
              >
                {tab.value === 'later' && (
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                {tab.label}
                {count !== null && count > 0 && (
                  <span className={clsx(
                    'text-xs px-1.5 py-0.5 rounded-full',
                    statusFilter === tab.value ? 'bg-indigo-100' : 'bg-slate-100'
                  )}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Search bar */}
        <div className={clsx('relative', isV3 ? 'inline-block w-72 ml-4 align-middle' : 'mt-3')}>
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Zoeken op naam, email, onderwerp..."
            value={searchInput}
            onChange={(e) => {
              const val = e.target.value;
              setSearchInput(val);
              if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
              searchTimerRef.current = setTimeout(() => setSearchQuery(val), 300);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setSearchInput('');
                setSearchQuery('');
                (e.target as HTMLInputElement).blur();
              }
            }}
            className="w-full pl-9 pr-8 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          {searchInput && (
            <button
              onClick={() => { setSearchInput(''); setSearchQuery(''); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {searchQuery && (
          <div className="mt-2 flex items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 font-medium">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              "{searchQuery}" \u2014 zoekt in alle statussen
            </span>
          </div>
        )}

        {!searchQuery && !isV3 && (
          <div className="mt-2 flex items-center gap-3 text-[10px] text-slate-400">
            <span><kbd className="px-1 py-0.5 bg-slate-100 rounded text-[10px]">{'\u2191\u2193'}</kbd> navigeren</span>
            <span><kbd className="px-1 py-0.5 bg-slate-100 rounded text-[10px]">Enter</kbd> openen</span>
            <span><kbd className="px-1 py-0.5 bg-slate-100 rounded text-[10px]">E</kbd> afhandelen</span>
            <span><kbd className="px-1 py-0.5 bg-slate-100 rounded text-[10px]">I</kbd> in behandeling</span>
            <button
              type="button"
              onClick={() => setShowShortcutHelp(true)}
              className="hover:text-slate-600"
              title="Alle sneltoetsen"
            >
              <kbd className="px-1 py-0.5 bg-slate-100 rounded text-[10px]">?</kbd> alle
            </button>
          </div>
        )}
      </div>

      {/* Conversation list */}
      {threadsError && <p role="alert" className="px-6 py-2 text-xs text-red-700 bg-red-50">{threadsError}</p>}
      {classicError && <p role="alert" className="px-6 py-2 text-xs text-red-700 bg-red-50">{classicError}</p>}
      {isV2 && isWerkvoorraad && nuCount > 0 && (
        <div data-inbox-laag="nu" className="px-6 py-2 flex items-center gap-2 border-b border-red-100 bg-red-50 text-xs text-red-800">
          <span className="font-semibold uppercase tracking-wide">Nu</span>
          <span>{nuCount} {nuCount === 1 ? 'gesprek' : 'gesprekken'}</span>
          <span className="text-red-600">· fouten eerst, daarna oudste deadline</span>
        </div>
      )}
      <div className="flex-1 overflow-y-auto" ref={listRef}>
        {isLoading && conversations.length === 0 ? (
          <div className="divide-y divide-slate-100">
            {Array.from({ length: 8 }).map((_, i) => (
              <ConversationSkeleton key={i} />
            ))}
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-400">
            <svg className="w-12 h-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
            <p className="text-sm">{threadsError ? 'Het overzicht is niet beschikbaar' : 'Geen gesprekken gevonden'}</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {getoondeConversations.map((conv, idx) => {
              const isActive = activeId === conv.id;
              return (
                <button
                  key={conv.id}
                  data-conv-item
                  onClick={() => handleSelect(conv.id)}
                  className={clsx(
                    'w-full flex items-start gap-3 text-left group',
                    'transition-all duration-200 ease-out',
                    dismissedIds.has(conv.id)
                      ? 'opacity-0 max-h-0 px-6 py-0 overflow-hidden'
                      : isV3 ? 'opacity-100 max-h-48 px-4 py-2' : 'opacity-100 max-h-48 px-5 py-3',
                    isActive
                      ? 'bg-indigo-50 border-l-2 border-indigo-500 pl-[22px]'
                      : selectedIndex === idx
                        ? 'bg-slate-50'
                        : 'hover:bg-slate-50'
                  )}
                >
                  {isV3 ? (() => {
                    const w = v3Werkstand(conv);
                    const open = conv.status !== 'afgehandeld' && conv.status !== 'bounce';
                    const naam = conv.customer_name || conv.from_name || conv.from_email || 'Onbekend';
                    return (
                      <div className="w-full grid grid-cols-[10px_200px_minmax(0,1fr)_auto] items-center gap-3">
                        <div className="w-2.5 h-2.5 flex items-center justify-center">
                          {conv.channel === 'whatsapp' ? (
                            <svg className="w-3 h-3 text-emerald-500" viewBox="0 0 24 24" fill="currentColor" aria-label="WhatsApp"><path d="M12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0012.05 0zm0 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884z"/></svg>
                          ) : conv.has_unread && open ? (
                            <div className="w-2 h-2 rounded-full bg-indigo-500" title="Ongelezen" />
                          ) : null}
                        </div>
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className={clsx('text-sm truncate', open ? 'font-semibold text-slate-900' : 'text-slate-700')}>{naam}</span>
                          <span className={clsx('px-1 py-px rounded text-[9px] font-medium flex-shrink-0 uppercase tracking-wide', BRAND_COLORS[conv.brand] || 'bg-slate-100 text-slate-600')}>
                            {conv.brand === 'SCHOONHUISSHOP' ? 'SHS' : conv.brand === 'VLOERKLEEDREINIGEN' ? 'VKR' : conv.brand}
                          </span>
                          {conv.assigned_to && (
                            <span title={`Toegewezen aan ${resolveAssigneeName(conv.assigned_to, teamMembers)}`} className="px-1 py-px rounded text-[10px] font-semibold flex-shrink-0 bg-indigo-100 text-indigo-700">
                              {assigneeInitials(conv.assigned_to, teamMembers)}
                            </span>
                          )}
                        </div>
                        <p className="text-sm truncate min-w-0">
                          {conv.channel === 'whatsapp' ? (
                            <span className={clsx(open ? 'text-slate-900' : 'text-slate-600')}>{schoneSnippet(conv.snippet) || conv.subject || ''}</span>
                          ) : (<>
                            <span className={clsx(open ? 'font-semibold text-slate-900' : 'text-slate-700')}>{conv.subject || '(Geen onderwerp)'}</span>
                            {schoneSnippet(conv.snippet) && <span className="text-slate-400"> · {schoneSnippet(conv.snippet)}</span>}
                          </>)}
                        </p>
                        <div className="flex items-center justify-end gap-3 flex-shrink-0 min-w-[190px]">
                          {open && (
                            <div className="hidden group-hover:flex items-center gap-1">
                              <SnoozeMenu conversationId={conv.id} snoozedUntil={conv.snoozed_until} variant="compact" />
                              {conv.status !== 'in_behandeling' && (
                                <button onClick={(e) => { e.stopPropagation(); handleDismiss(conv.id, 'in_behandeling'); }}
                                  className="text-xs font-medium px-2 py-1 rounded bg-amber-50 text-amber-700 hover:bg-amber-100 whitespace-nowrap">In behandeling</button>
                              )}
                              <button onClick={(e) => { e.stopPropagation(); handleDismiss(conv.id, 'afgehandeld'); }}
                                className="text-xs font-medium px-2 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 whitespace-nowrap">Afhandelen</button>
                            </div>
                          )}
                          {conv.has_attachments && (
                            <svg className="w-3.5 h-3.5 text-slate-400 group-hover:hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-label="Bijlage"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" /></svg>
                          )}
                          {w && open && <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap group-hover:hidden', w.klas)}>{w.label}</span>}
                          {statusFilter === 'later' && conv.snoozed_until ? (
                            <span className="text-xs text-indigo-600 font-medium group-hover:hidden whitespace-nowrap">{formatSnoozeUntil(conv.snoozed_until)}</span>
                          ) : (
                            <span className="text-xs text-slate-400 group-hover:hidden whitespace-nowrap tabular-nums w-14 text-right" title={conv.message_count > 1 ? `${conv.message_count} berichten` : undefined}>
                              {formatTime(conv.last_message_at)}{conv.message_count > 1 ? ` · ${conv.message_count}` : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })() : (<>
                  {/* Status indicator dot */}
                  <div className="pt-2 w-2 flex-shrink-0">
                    {conv.status !== 'afgehandeld' && (
                      <div className={clsx('w-2 h-2 rounded-full', STATUS_COLORS[conv.status] || 'bg-blue-500')} />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      {conv.channel === 'phone' ? (
                        <svg className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                        </svg>
                      ) : conv.channel === 'whatsapp' ? (
                        <svg className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                      ) : (
                        <svg className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                        </svg>
                      )}
                      {conv.prebaked_draft && !isV3 && (!isV2 || !conv.mail_werkstap) && (
                        <span title="AI-concept klaar" className="text-amber-500 flex-shrink-0">✨</span>
                      )}
                      {/* Naam is geen link meer naar /klanten: dat verliet de inbox
                          (rondgang 14-09). De klant open je vanuit de klantkaart. */}
                      <span className="font-medium text-sm truncate text-slate-900">
                        {conv.customer_name || conv.from_name || conv.from_email || 'Onbekend'}
                      </span>
                      <span className={clsx(
                        'px-1 py-px rounded text-[10px] font-medium flex-shrink-0 uppercase tracking-wide',
                        BRAND_COLORS[conv.brand] || 'bg-slate-100 text-slate-600'
                      )}>
                        {conv.brand === 'SCHOONHUISSHOP' ? 'SHS' : conv.brand === 'VLOERKLEEDREINIGEN' ? 'VKR' : conv.brand}
                      </span>
                      {conv.assigned_to && (
                        <span
                          title={`Toegewezen aan ${resolveAssigneeName(conv.assigned_to, teamMembers)}`}
                          className="px-1.5 py-0.5 rounded text-xs font-semibold flex-shrink-0 bg-indigo-100 text-indigo-700"
                        >
                          {assigneeInitials(conv.assigned_to, teamMembers)}
                        </span>
                      )}
                    </div>
                    <p className="text-sm truncate text-slate-700">
                      {conv.subject || '(Geen onderwerp)'}
                    </p>
                    <p className="text-xs text-slate-400 truncate mt-0.5">
                      {conv.snippet}
                    </p>
                    {isV2 && <div className="mt-1 flex flex-wrap gap-1">
                      {conv.mail_werkstap && (
                        <span aria-label={`Afhandeling: ${conv.mail_werkstap.label}`} title={conv.mail_werkstap.uitleg}
                          className={clsx('inline-block rounded px-1.5 py-0.5 text-xs',
                            conv.mail_werkstap.toon === 'rood' ? 'bg-red-50 text-red-700'
                              : conv.mail_werkstap.toon === 'amber' ? 'bg-amber-50 text-amber-800'
                                : conv.mail_werkstap.toon === 'groen' ? 'bg-emerald-50 text-emerald-700' : 'bg-indigo-50 text-indigo-700')}>
                          {conv.mail_werkstap.label}
                        </span>
                      )}
                      {conv.inbox_meta?.urgentie.nu && (
                        <span aria-label="Urgentie: Nu" title={conv.inbox_meta.urgentie.redenen.join(', ')} className="rounded px-1.5 py-0.5 text-xs font-semibold bg-red-600 text-white">Nu</span>
                      )}
                      {conv.inbox_meta?.ongedekt.actief && (
                        <span aria-label="Ongedekte handeling" className="rounded px-1.5 py-0.5 text-xs bg-amber-100 text-amber-900">Ongedekt {conv.inbox_meta.ongedekt.aantal || ''}</span>
                      )}
                      {conv.inbox_meta?.duplicate && (
                        <span aria-label="Mogelijk dubbel gesprek" title={conv.inbox_meta.duplicate.crossBrand ? 'Cross-brand: kies het antwoordmerk handmatig' : 'Kies één canoniek antwoord'} className="rounded px-1.5 py-0.5 text-xs bg-violet-100 text-violet-800">
                          Mogelijk dubbel · {conv.inbox_meta.duplicate.aantal}
                        </span>
                      )}
                      {conv.inbox_meta?.assignee.status === 'niet_toegewezen' && (
                        <span className="rounded px-1.5 py-0.5 text-xs bg-slate-100 text-slate-600">Niet toegewezen</span>
                      )}
                    </div>}
                  </div>

                  {/* Meta + hover actions */}
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    {statusFilter === 'later' && conv.snoozed_until ? (
                      <span className="text-xs text-indigo-600 font-medium group-hover:hidden inline-flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {formatSnoozeUntil(conv.snoozed_until)}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400 group-hover:hidden whitespace-nowrap" title={conv.message_count > 1 ? `${conv.message_count} berichten` : undefined}>
                        {formatTime(conv.last_message_at)}{conv.message_count > 1 ? ` · ${conv.message_count}` : ''}
                      </span>
                    )}
                    {isV3 && (() => { const w = v3Werkstand(conv); return w && conv.status !== 'afgehandeld' && conv.status !== 'bounce'
                      ? <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap group-hover:hidden', w.klas)}>{w.label}</span> : null; })()}

                    {conv.status !== 'afgehandeld' && conv.status !== 'bounce' && (
                      <div className="hidden group-hover:flex items-center gap-1">
                        <SnoozeMenu
                          conversationId={conv.id}
                          snoozedUntil={conv.snoozed_until}
                          variant="compact"
                        />
                        {conv.status !== 'in_behandeling' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDismiss(conv.id, 'in_behandeling');
                            }}
                            className="text-xs font-medium px-2 py-1 rounded bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors whitespace-nowrap"
                          >
                            In behandeling
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isV2 || !conv.inbox_meta?.blokkeertAfhandelen) handleDismiss(conv.id, 'afgehandeld');
                          }}
                          disabled={isV2 && conv.inbox_meta?.blokkeertAfhandelen}
                          title={isV2 && conv.inbox_meta?.blokkeertAfhandelen ? 'Los eerst de zichtbare werkstap op' : 'Afhandelen'}
                          className="text-xs font-medium px-2 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors flex items-center gap-1 whitespace-nowrap disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                        >
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {isV2 && conv.inbox_meta?.blokkeertAfhandelen ? 'Eerst herstellen' : 'Afhandelen'}
                        </button>
                      </div>
                    )}
                  </div>
                  </>)}
                </button>
              );
            })}
            {hasMore && (
              <>
                <div ref={loadMoreSentinelRef} aria-hidden />
                <button
                  onClick={() => void loadMore()}
                  disabled={isLoadingMore}
                  className="w-full py-3 text-sm font-medium text-indigo-600 hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {isLoadingMore ? (
                    <>
                      <span className="w-3 h-3 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                      Laden...
                    </>
                  ) : (
                    'Meer laden'
                  )}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <ShortcutHelp open={showShortcutHelp} onClose={() => setShowShortcutHelp(false)} />
      <OutboundComposerModal open={showNewEmail} onClose={() => setShowNewEmail(false)} />
    </div>
  );
}
